import { createSlice } from "@reduxjs/toolkit";
import { IStartWithPANForm } from "../../Pages/PanInformation/StartWithPanForm";
import api from "../../Services/ApiServices";
import AuthInfoContext from "../../Types/AuthInfoContext";
import { SetPANDOBInsertObject } from "../../Types/InsertObjects";
import { IOTPSession } from "../../Types/OTPSession";
import { dispatch, RootState } from "../store";
import { setAuthInfo, setSelectedAOR, setStage } from "./common";

export enum ExistingClientStep {
  SEND_OTP = "SEND_OTP",
  CONFIRM_OTP = "CONFIRM_OTP",
}

interface IStep1State {
  isLoading: boolean;
  error: any;
  objResponse?: IOTPSession;
  existingClientOTPSessionLoading?: boolean;
  existingClientOTPSessionError?: any;
  existingClientOTPSession?: IOTPSession;
  existingClientStep: ExistingClientStep;
  otpSendDateTime?: any;
}

const initialState: IStep1State = {
  isLoading: false,
  error: null,
  existingClientStep: ExistingClientStep.SEND_OTP,
};

const slice = createSlice({
  name: "stage1",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = undefined;
    },
    panRequestSuccess(state, action) {
      state.objResponse = action.payload;
      state.isLoading = false;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    existingClientOTPSessionStartLoading(state) {
      state.existingClientOTPSessionLoading = true;
      state.existingClientOTPSessionError = undefined;
    },
    existingClientOTPSessionHasError(state, action) {
      state.existingClientOTPSessionLoading = false;
      state.existingClientOTPSessionError = action.payload;
    },
    existingClientOTPSessionSuccess(state, action) {
      state.existingClientOTPSession = action.payload;
      state.existingClientOTPSessionLoading = false;
      state.otpSendDateTime = new Date();
    },
    setExistingClientStep(state, action) {
      state.existingClientStep = action.payload;
    },
    resetFields() {
      return { ...initialState };
    },
  },
});

// Reducer
export default slice.reducer;

export const { resetFields } = slice.actions;

export const setObjPANFormData = (objPANFormData: IStartWithPANForm) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      await dispatch(slice.actions.startLoading());
      const { objAuthInfoResponse } = getState().common;
      const io = new SetPANDOBInsertObject();
      // io.CDOB = utils.convertToYYYYMMDDFormat(objPANFormData.dob);
      io.CDOB = objPANFormData.dob ? objPANFormData.dob : "";
      io.CPAN = objPANFormData.pan;
      io.CGMF = objPANFormData.gender;
      io.BPC = objPANFormData.BrokerageProductCode;
      io.DAMCSC = objPANFormData.DematAMCSchemeCode;
      io.SIDISB = objPANFormData.ShouldIssueDISBooklet === "1" ? 1 : 0;
      io.AOSAI = JSON.stringify(objAuthInfoResponse);

      let response: IOTPSession = await api.setPANDOB(io);

      console.log(response);
      const objAuthContext = new AuthInfoContext(
        response.ContextSystemObjectIDOrCode
      );

      await dispatch(slice.actions.panRequestSuccess(response));

      if (!objAuthContext.Status)
        throw new Error(objAuthContext.Message || "Unexpected error.");
      if (response.MemoryBag?.Records && response.MemoryBag?.Records[0]) {
        await dispatch(setSelectedAOR(response.MemoryBag?.Records[0]));
      }
      await dispatch(setAuthInfo(response));
      if (objAuthContext.NextStage > 1) {
        await dispatch(setStage(objAuthContext.NextStage));
      } else {
        throw new Error("Unexpected stage identified.");
      }
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.hasError(ex));
    }
  };
};

export const sendExistingClientOTP = () => {
  return async (disp: any, getState: () => RootState) => {
    try {
      const { objAuthInfoResponse } = getState().common;
      await dispatch(slice.actions.existingClientOTPSessionStartLoading());
      let response = await api.SendOTPForExistingClient({
        AOSAI: JSON.stringify(objAuthInfoResponse),
      });

      const objAuthContext = new AuthInfoContext(
        response.ContextSystemObjectIDOrCode
      );

      if (!objAuthContext.Status) throw new Error(objAuthContext.Message);
      const MemoryBag: any = response.MemoryBag;
      await dispatch(setAuthInfo(response));
      if (MemoryBag) {
        await dispatch(
          slice.actions.existingClientOTPSessionSuccess(MemoryBag)
        );
        await dispatch(
          slice.actions.setExistingClientStep(ExistingClientStep.CONFIRM_OTP)
        );
      } else {
        throw new Error("Unexpected Stage instruction");
      }
    } catch (ex) {
      dispatch(slice.actions.existingClientOTPSessionHasError(ex));
    }
  };
};

export const resendExistingClientOTP = () => {
  return async (disp: any, getState: () => RootState) => {
    try {
      const { existingClientOTPSession } = getState().stage1;
      const { objAuthInfoResponse } = getState().common;
      if (!existingClientOTPSession)
        throw new Error(`OTP session is not found`);

      await dispatch(slice.actions.existingClientOTPSessionStartLoading());
      let response = await api.ResendOTPForExistingClient({
        SOTPR: JSON.stringify(existingClientOTPSession),
        AOSAI: JSON.stringify(objAuthInfoResponse),
      });
      if (!response.Status) throw new Error(response.Message);
      await dispatch(slice.actions.existingClientOTPSessionSuccess(response));
      if (!response.Status) throw new Error(response.Message);
    } catch (ex) {
      dispatch(slice.actions.existingClientOTPSessionHasError(ex));
    }
  };
};

export const verifyExistingClientOTP = (
  mobileOTP: string,
  emailOTP: string
) => {
  return async (disp: any, getState: () => RootState) => {
    try {
      const { existingClientOTPSession } = getState().stage1;
      const { objAuthInfoResponse, objAuthContext: cr } = getState().common;
      console.log(cr);
      // if (!existingClientOTPSession)
      //   throw new Error(`OTP session is not found`);
      await dispatch(slice.actions.existingClientOTPSessionStartLoading());
      console.log(existingClientOTPSession, objAuthInfoResponse);
      let response: IOTPSession = await api.verifyOTPForExistingClient({
        SOTPR: existingClientOTPSession
          ? JSON.stringify(existingClientOTPSession)
          : "",
        AOSAI: JSON.stringify(objAuthInfoResponse),
        EACAONA: 1,
        MOTP: mobileOTP || "",
        EOTP: emailOTP || "",
      });
      const objAuthContext = new AuthInfoContext(
        response.ContextSystemObjectIDOrCode
      );

      // await dispatch(slice.actions.existingClientOTPSessionSuccess(response));
      if (!objAuthContext.Status)
        throw new Error(objAuthContext.Message || "Unexpected Error");
      if (!response.WasOTPValid)
        throw new Error(response.OTPVerificationMessage || "OTP was not valid");

      await dispatch(setAuthInfo(response));

      // Set AOR
      if (response.MemoryBag?.Records && response.MemoryBag?.Records[0])
        await dispatch(setSelectedAOR(response.MemoryBag?.Records[0]));

      if (objAuthContext.NextStage)
        await dispatch(setStage(objAuthContext.NextStage));
      else throw new Error("Unexpected next state instruction");
    } catch (ex) {
      console.log(ex, "ex");
      await dispatch(slice.actions.existingClientOTPSessionHasError(ex));
    }
  };
};
